<template>
  <div class="read-state">
    <!-- Loading wins over a stale error: a retry in flight must look like progress,
         not like the failure it is trying to clear. -->
    <div v-if="showLoading" class="read-state__panel read-state__loading">
      <slot name="loading">
        <LumiLoader size="panel" :label="loadingLabel" />
      </slot>
    </div>

    <div v-else-if="showError" class="read-state__panel">
      <slot name="error" :message="message" :retry="retry">
        <InlineNotice variant="error" :title="t('errors.loadFailedTitle')">
          {{ message }}
          <template v-if="retry" #action>
            <Button variant="secondary" size="sm" @click="retry">{{ t('common.retry') }}</Button>
          </template>
        </InlineNotice>
      </slot>
    </div>

    <slot v-else-if="showEmpty" name="empty">
      <EmptyState :title="emptyTitle ?? t('common.nothingHere')" :description="emptyDescription">
        <template v-if="$slots['empty-action']" #action><slot name="empty-action" /></template>
      </EmptyState>
    </slot>

    <template v-else>
      <!-- Fourth mode: content is being kept AND a read is in flight over it — a
           filter change, a page turn, a month flip. Quiet: a small spinner and label
           above the content, not the first-load panel and not a takeover. Outranks a
           stale-failure notice for the same reason `showLoading` outranks `showError`
           above — a retry in flight must look like progress, not like the failure it
           is trying to clear, and an ordinary refresh clearing a previous stale
           notice is no different. `showStale` already requires `!loading`, so the two
           are mutually exclusive by construction; this is not a race. -->
      <div v-if="showRefreshing" class="read-state__refreshing">
        <slot name="refreshing">
          <!-- The visible label already carries the announcement; giving the Spinner
               its own `aria-label` too would have a screen reader hear it twice. -->
          <Spinner size="sm" />
          <span v-if="loadingLabel" class="read-state__refreshing-label">{{ loadingLabel }}</span>
        </slot>
      </div>
      <!-- Third mode: the read failed over rows that are still on screen. The notice
           goes ABOVE the content instead of in place of it, and it is a `warning`, not
           an error — nothing was taken away, what is shown may just be older than the
           user thinks. -->
      <div v-else-if="showStale" class="read-state__panel">
        <slot name="stale" :message="message" :retry="retry">
          <InlineNotice variant="warning" :title="t('errors.refreshFailedTitle')">
            {{ message }}
            <template v-if="retry" #action>
              <Button variant="secondary" size="sm" @click="retry">{{ t('common.retry') }}</Button>
            </template>
          </InlineNotice>
        </slot>
      </div>
      <slot />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, EmptyState, InlineNotice, LumiLoader, Spinner } from '@lumi/ui';
import { apiErrorMessage } from '../utils/apiError';

// The suite's default in-place read-failure state (issue 195,
// docs/adr/0068-suite-feedback-seam-and-read-rule.md rule 5).
//
// The interceptor no longer toasts a failed GET, because a floating toast about a read
// is feedback the user cannot connect to anything on screen. The failure belongs in the
// surface that was loading — which would be an obligation on every one of the suite's
// hundreds of reads if there were no shared default. This is that default: wrap the
// surface, hand it `loading` / `error` / `empty`, and it renders the glyph loader (`LumiLoader` — the wait that owns a panel),
// an inline error with a retry affordance, or an empty state; the quiet refresh line over
// kept content stays the in-place dots. Every branch is an overridable slot
// for a surface that needs its own shape.
//
// `error` is whatever was thrown. The string shown is the LOCALIZED errors.* one
// (`apiErrorMessage`), never the backend's own message (ADR 0036 rule 5).
const props = withDefaults(
  defineProps<{
    /**
     * Required, not optional: a sweep of this component's call sites at issue 195
     * found roughly half of them never bound it, so first load rendered the EMPTY
     * state — a panel asserting there is no data — while the read was still in
     * flight (S21e). When half the authors get it wrong, the shape was wrong, not
     * the authors. Do not "helpfully" give this a default; a call site with
     * genuinely no loading concept should say so explicitly with `:loading="false"`
     * and a comment, not opt out silently.
     */
    loading: boolean;
    /** The thrown value from the failed read; null/undefined once it succeeded. */
    error?: unknown;
    /**
     * Required, not optional: `empty` is not merely "the read returned nothing" — it
     * is this component's ONLY signal for "is there content on screen right now"
     * (S21g). Leave it unbound and it defaults to `false`, so `keeping` is true from
     * the very first millisecond, before any request has returned, and a genuine
     * first load renders the quiet refreshing line instead of the loading panel —
     * the surface's only loading feedback, gone.
     *
     * Its escape hatch is NOT as safe as `loading`'s: `:loading="false"` is honest on
     * a surface with no loading concept, but `:empty="false"` is almost never honest
     * — it asserts there is content on screen at a moment when there may be none, and
     * it costs the surface its loading panel. On a single-record surface the honest
     * binding is almost always `:empty="!record"`.
     */
    empty: boolean;
    emptyTitle?: string;
    emptyDescription?: string;
    /** Omit to render the failure without a retry button (a read that cannot be repeated). */
    retry?: () => unknown;
    /**
     * Opt OUT of keeping rows across a failed read — a failed refresh, filter or page
     * turn replaces the surface with the failure instead of keeping content on screen
     * with a warning above it (S21c: keeping is the default, this is the escape hatch).
     * Clearing rows the user can still act on punishes them for a failed refresh, so
     * this should stay off for ordinary lists and tables. Reach for it only when the
     * surface cannot make sense partially updated — e.g. a wizard step or a single-
     * record detail view where "old" content isn't a safe thing to keep looking at,
     * or where the caller genuinely wants the failure to take the whole region.
     *
     * It cannot contradict the empty case: with `empty` also true there is nothing to
     * be stale, and the failure takes the surface regardless of this prop.
     */
    replaceContentOnError?: boolean;
  }>(),
  // No default for `error`: it is typed `unknown`, and Vue would demand a FACTORY for an
  // object-typed default. Undefined is the natural "nothing failed" anyway.
  { replaceContentOnError: false },
);

const { t, te } = useI18n();

// `common.loading` is an APP key, not an app-kit one: seven of the nine apps already
// define it, so app-kit cannot own it without shadowing them (check-i18n-no-shadow).
// Where it is missing, `undefined` leaves the loader unlabelled (neither Spinner nor
// LumiLoader has a default label: component-lib has no i18n of its own).
const loadingLabel = computed(() => (te('common.loading') ? t('common.loading') : undefined));

// `null` and `undefined` both mean "no failure"; anything else is one — including the
// falsy-looking values a hand-rolled catch sometimes stores (0, ''), which are still a
// failure someone recorded.
const failed = computed(() => props.error !== null && props.error !== undefined);
const message = computed(() => apiErrorMessage(props.error));

// Five states, as five mutually exclusive branches. `keeping` is the whole of the
// difference: with content to keep, neither `loading` nor `error` may take the surface
// away.
//
//   1. First load (`loading`, nothing to keep) — the loading panel takes the surface.
//   2. First-load failure (`error`, nothing to keep) — the error panel takes the
//      surface.
//   3. Empty (succeeded, `empty`, nothing to keep) — the empty state takes the
//      surface.
//   4. Content, no error, not loading — the default slot alone.
//   5a. Refresh over content (`loading` while keeping) — a quiet spinner+label above
//       the content, which stays exactly as it was.
//   5b. Failure over content, not loading (`error` while keeping) — a `warning`
//       notice above the content instead of a takeover: nothing was taken away, what
//       is shown may just be older than the user thinks.
//
// 5a outranks 5b: a retry in flight must look like progress, not like the failure it
// is trying to clear, so a stale notice from a previous failed refresh disappears the
// moment the next refresh starts, same as `showLoading` outranking `showError` above.
// Keeping is the default: it only turns off when the surface opts out
// (`replaceContentOnError`), or when there is nothing to keep (`empty`), which is when
// a failure legitimately takes the region.
const keeping = computed(() => !props.replaceContentOnError && !props.empty);
const showLoading = computed(() => props.loading && !keeping.value);
const showError = computed(() => !showLoading.value && failed.value && !keeping.value);
const showEmpty = computed(() => !showLoading.value && !showError.value && props.empty);
const showRefreshing = computed(() => keeping.value && props.loading);
const showStale = computed(() => keeping.value && failed.value && !props.loading);
</script>

<style scoped>
.read-state__panel {
  padding: 1rem;
}
.read-state__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}
.read-state__refreshing {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
}
.read-state__refreshing-label {
  font-size: var(--text-xs);
  color: var(--text-muted);
}
</style>
